import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Trophy, ArrowLeft, Calendar, BarChart3, Users, Swords, Clock, Copy, Check, Shield, TrendingUp, MapPin } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

function LiveWidget({ type, params = {}, height = 500 }) {
  const query = new URLSearchParams({ type, ...params }).toString();
  return (
    <div className="rounded-xl border overflow-hidden bg-white">
      <iframe
        src={`/widget-embed.html?${query}`}
        className="w-full border-0"
        style={{ height: `${height}px` }}
        title={`${type} widget`}
      />
    </div>
  );
}

export default function WidgetDemo() {
  const [copied, setCopied] = useState(null);

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopied(id);
    toast.success('Embed code copied!');
    setTimeout(() => setCopied(null), 2000);
  };

  const CodeBlock = ({ code, id }) => (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-muted-foreground">EMBED CODE</span>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => copyCode(code, id)}>
          {copied === id ? <><Check className="h-3 w-3 mr-1" />Copied</> : <><Copy className="h-3 w-3 mr-1" />Copy</>}
        </Button>
      </div>
      <pre className="bg-gray-900 text-gray-300 text-[11px] p-3 rounded-lg overflow-x-auto font-mono leading-relaxed">{code}</pre>
    </div>
  );

  // Sample data
  const sampleFixtures = [
    { home: 'Arsenal', hLogo: 'https://media.api-sports.io/football/teams/42.png', hScore: 2, away: 'Chelsea', aLogo: 'https://media.api-sports.io/football/teams/49.png', aScore: 1, status: 'FT' },
    { home: 'Liverpool', hLogo: 'https://media.api-sports.io/football/teams/40.png', hScore: 3, away: 'Manchester City', aLogo: 'https://media.api-sports.io/football/teams/50.png', aScore: 0, status: 'FT' },
    { home: 'Aston Villa', hLogo: 'https://media.api-sports.io/football/teams/66.png', hScore: 1, away: 'Newcastle', aLogo: 'https://media.api-sports.io/football/teams/34.png', aScore: 1, status: 'FT' },
    { home: 'Brentford', hLogo: 'https://media.api-sports.io/football/teams/55.png', hScore: null, away: 'Brighton', aLogo: 'https://media.api-sports.io/football/teams/51.png', aScore: null, status: 'NS', time: 'Sat 15:00' },
    { home: 'Everton', hLogo: 'https://media.api-sports.io/football/teams/45.png', hScore: null, away: 'Fulham', aLogo: 'https://media.api-sports.io/football/teams/36.png', aScore: null, status: 'NS', time: 'Sat 17:30' },
  ];

  const sampleStandings = [
    { pos: 1, team: 'Arsenal', logo: 'https://media.api-sports.io/football/teams/42.png', p: 30, w: 20, d: 7, l: 3, gd: '+42', pts: 67 },
    { pos: 2, team: 'Manchester City', logo: 'https://media.api-sports.io/football/teams/50.png', p: 29, w: 18, d: 6, l: 5, gd: '+35', pts: 60 },
    { pos: 3, team: 'Manchester United', logo: 'https://media.api-sports.io/football/teams/33.png', p: 29, w: 14, d: 9, l: 6, gd: '+18', pts: 51 },
    { pos: 4, team: 'Aston Villa', logo: 'https://media.api-sports.io/football/teams/66.png', p: 29, w: 15, d: 6, l: 8, gd: '+15', pts: 51 },
    { pos: 5, team: 'Chelsea', logo: 'https://media.api-sports.io/football/teams/49.png', p: 29, w: 13, d: 9, l: 7, gd: '+12', pts: 48 },
    { pos: 6, team: 'Liverpool', logo: 'https://media.api-sports.io/football/teams/40.png', p: 29, w: 14, d: 6, l: 9, gd: '+14', pts: 48 },
    { pos: 7, team: 'Brentford', logo: 'https://media.api-sports.io/football/teams/55.png', p: 29, w: 13, d: 5, l: 11, gd: '+5', pts: 44 },
    { pos: 8, team: 'Everton', logo: 'https://media.api-sports.io/football/teams/45.png', p: 29, w: 12, d: 7, l: 10, gd: '+2', pts: 43 },
  ];

  const arsenalSquad = [
    { name: 'B. Saka', pos: 'Forward', num: 7, goals: 14, assists: 8 },
    { name: 'K. Havertz', pos: 'Forward', num: 29, goals: 11, assists: 5 },
    { name: 'M. Odegaard', pos: 'Midfielder', num: 8, goals: 7, assists: 10 },
    { name: 'D. Rice', pos: 'Midfielder', num: 41, goals: 5, assists: 4 },
    { name: 'W. Saliba', pos: 'Defender', num: 2, goals: 2, assists: 1 },
    { name: 'G. Jesus', pos: 'Forward', num: 9, goals: 8, assists: 3 },
  ];

  const scheduleData = [
    { gw: 31, date: 'Mar 8', home: 'Arsenal', hLogo: 'https://media.api-sports.io/football/teams/42.png', away: 'Brentford', aLogo: 'https://media.api-sports.io/football/teams/55.png', time: '15:00' },
    { gw: 31, date: 'Mar 8', home: 'Chelsea', hLogo: 'https://media.api-sports.io/football/teams/49.png', away: 'Wolves', aLogo: 'https://media.api-sports.io/football/teams/39.png', time: '15:00' },
    { gw: 31, date: 'Mar 8', home: 'Sunderland', hLogo: 'https://media.api-sports.io/football/teams/746.png', away: 'Liverpool', aLogo: 'https://media.api-sports.io/football/teams/40.png', time: '17:30' },
    { gw: 31, date: 'Mar 9', home: 'Manchester City', hLogo: 'https://media.api-sports.io/football/teams/50.png', away: 'Everton', aLogo: 'https://media.api-sports.io/football/teams/45.png', time: '14:00' },
    { gw: 32, date: 'Mar 15', home: 'Newcastle', hLogo: 'https://media.api-sports.io/football/teams/34.png', away: 'Arsenal', aLogo: 'https://media.api-sports.io/football/teams/42.png', time: '12:30' },
    { gw: 32, date: 'Mar 15', home: 'Liverpool', hLogo: 'https://media.api-sports.io/football/teams/40.png', away: 'Manchester United', aLogo: 'https://media.api-sports.io/football/teams/33.png', time: '17:30' },
  ];

  const h2hData = [
    { date: 'Oct 2025', home: 'Chelsea', hScore: 1, away: 'Arsenal', aScore: 1, comp: 'EPL' },
    { date: 'Apr 2025', home: 'Arsenal', hScore: 3, away: 'Chelsea', aScore: 1, comp: 'EPL' },
    { date: 'Oct 2024', home: 'Chelsea', hScore: 1, away: 'Arsenal', aScore: 0, comp: 'EPL' },
    { date: 'Apr 2024', home: 'Arsenal', hScore: 5, away: 'Chelsea', aScore: 0, comp: 'EPL' },
    { date: 'Oct 2023', home: 'Chelsea', hScore: 2, away: 'Arsenal', aScore: 2, comp: 'EPL' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 flex h-[64px] sm:h-[120px] items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg">
            <img src="/poolsfc-logo.png" alt="Pools FC" className="h-[48px] w-[48px] sm:h-[104px] sm:w-[104px] rounded-lg" />
            Pools FC
          </Link>
          <Button variant="ghost" size="sm" asChild><Link to="/"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link></Button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <Badge className="bg-[#f3f0ff] text-[#5b3cc4] font-medium mb-3">PREMIUM FEATURE</Badge>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Live EPL Widgets</h1>
          <p className="text-muted-foreground max-w-lg mx-auto">Real-time data embedded directly in your pool page. Available on paid plans.</p>
        </div>

        <Tabs defaultValue="fixtures" className="space-y-6">
          <TabsList className="mx-auto flex w-fit flex-wrap gap-1">
            <TabsTrigger value="fixtures"><Calendar className="h-3.5 w-3.5 mr-1" />Fixtures</TabsTrigger>
            <TabsTrigger value="standings"><BarChart3 className="h-3.5 w-3.5 mr-1" />Table</TabsTrigger>
            <TabsTrigger value="team"><Shield className="h-3.5 w-3.5 mr-1" />Team Profile</TabsTrigger>
            <TabsTrigger value="schedule"><Clock className="h-3.5 w-3.5 mr-1" />Schedule</TabsTrigger>
            <TabsTrigger value="h2h"><Swords className="h-3.5 w-3.5 mr-1" />Head-to-Head</TabsTrigger>
          </TabsList>

          {/* ===== FIXTURES ===== */}
          <TabsContent value="fixtures">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-[#5b3cc4]" />EPL Fixtures & Results</CardTitle>
                      <Badge className="bg-green-100 text-green-700 text-xs">LIVE DATA</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">Live scores, upcoming matches, recent results. Click any match for full details.</p>
                  </CardHeader>
                  <CardContent>
                    <LiveWidget type="games" params={{ league: '39', season: '2025' }} height={480} />
                    <CodeBlock code={`<api-sports-widget data-type="games"\n  data-league="39" data-season="2025">\n</api-sports-widget>`} id="fixtures" />
                  </CardContent>
                </Card>
              </div>
              <Card className="h-fit"><CardHeader><CardTitle className="text-base">Why It Matters</CardTitle></CardHeader><CardContent className="text-sm space-y-3">
                <p className="text-muted-foreground">Entrants check live scores on matchdays to see their pool points accumulate in real-time. Keeps them engaged and coming back every weekend.</p>
                <Badge className="bg-[#f3f0ff] text-[#5b3cc4]">Available: Basic tier+</Badge>
              </CardContent></Card>
            </div>
          </TabsContent>

          {/* ===== STANDINGS ===== */}
          <TabsContent value="standings">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between"><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#5b3cc4]" />EPL League Table</CardTitle><Badge className="bg-green-100 text-green-700 text-xs">LIVE DATA</Badge></div>
                  </CardHeader>
                  <CardContent>
                    <LiveWidget type="standings" params={{ league: '39', season: '2025' }} height={520} />
                    <CodeBlock code={`<api-sports-widget data-type="standings"\n  data-league="39" data-season="2025">\n</api-sports-widget>`} id="standings" />
                  </CardContent>
                </Card>
              </div>
              <Card className="h-fit"><CardHeader><CardTitle className="text-base">Why It Matters</CardTitle></CardHeader><CardContent className="text-sm space-y-3">
                <p className="text-muted-foreground">Entrants compare the real EPL table with the pool standings. A Tier 5 team climbing the real table means big pool points — creates excitement when underdogs perform well.</p>
                <Badge className="bg-[#f3f0ff] text-[#5b3cc4]">Available: Basic tier+</Badge>
              </CardContent></Card>
            </div>
          </TabsContent>

          {/* ===== TEAM PROFILE ===== */}
          <TabsContent value="team">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between"><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-[#5b3cc4]" />Team Profile — Arsenal</CardTitle><Badge className="bg-green-100 text-green-700 text-xs">LIVE DATA</Badge></div>
                    <p className="text-sm text-muted-foreground">Full squad, season stats, form, and venue info. Click any team in the pool to see this.</p>
                  </CardHeader>
                  <CardContent>
                    <LiveWidget type="team" params={{ team: '42' }} height={550} />
                    <CodeBlock code={`<api-sports-widget data-type="team"\n  data-team="42">\n</api-sports-widget>`} id="team" />
                  </CardContent>
                </Card>
              </div>
              <Card className="h-fit"><CardHeader><CardTitle className="text-base">Why It Matters</CardTitle></CardHeader><CardContent className="text-sm space-y-3">
                <p className="text-muted-foreground">Entrants click on any team they've picked to see the full squad, form, and stats. Perfect for pre-season research when choosing tier picks.</p>
                <p className="text-muted-foreground">During the season, it answers: "How is my Tier 3 pick Crystal Palace actually performing?"</p>
                <Badge className="bg-[#f3f0ff] text-[#5b3cc4]">Available: Basic tier+</Badge>
              </CardContent></Card>
            </div>
          </TabsContent>

          {/* ===== SCHEDULE ===== */}
          <TabsContent value="schedule">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between"><CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-[#5b3cc4]" />Season Schedule</CardTitle><Badge className="bg-green-100 text-green-700 text-xs">LIVE DATA</Badge></div>
                    <p className="text-sm text-muted-foreground">Full fixture calendar organized by matchweek. Navigate between rounds.</p>
                  </CardHeader>
                  <CardContent>
                    <LiveWidget type="league" params={{}} height={500} />
                    <CodeBlock code={`<api-sports-widget data-type="league"\n  data-league="39" data-season="2025">\n</api-sports-widget>`} id="schedule" />
                  </CardContent>
                </Card>
              </div>
              <Card className="h-fit"><CardHeader><CardTitle className="text-base">Why It Matters</CardTitle></CardHeader><CardContent className="text-sm space-y-3">
                <p className="text-muted-foreground">Entrants check when their picked teams play next. "My Tier 5 pick Sunderland plays Liverpool on Saturday — that's a tough one, probably 0 points this week."</p>
                <p className="text-muted-foreground">Builds anticipation and weekly engagement throughout the entire season.</p>
                <Badge className="bg-[#f3f0ff] text-[#5b3cc4]">Available: Starter tier+</Badge>
              </CardContent></Card>
            </div>
          </TabsContent>

          {/* ===== HEAD TO HEAD ===== */}
          <TabsContent value="h2h">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between"><CardTitle className="flex items-center gap-2"><Swords className="h-5 w-5 text-[#5b3cc4]" />Head-to-Head — Arsenal vs Chelsea</CardTitle><Badge className="bg-green-100 text-green-700 text-xs">LIVE DATA</Badge></div>
                    <p className="text-sm text-muted-foreground">Historical matchup results between any two teams.</p>
                  </CardHeader>
                  <CardContent>
                    <LiveWidget type="h2h" params={{ 'team-1': '42', 'team-2': '49' }} height={500} />
                    <CodeBlock code={`<api-sports-widget data-type="h2h"\n  data-team-1="42" data-team-2="49">\n</api-sports-widget>`} id="h2h" />
                  </CardContent>
                </Card>
              </div>
              <Card className="h-fit"><CardHeader><CardTitle className="text-base">Why It Matters</CardTitle></CardHeader><CardContent className="text-sm space-y-3">
                <p className="text-muted-foreground">Before a big fixture, entrants check: "Arsenal vs Chelsea this weekend — what's the historical record?" Builds anticipation for each matchweek.</p>
                <p className="text-muted-foreground">Creates pre-match discussions in the pool chat.</p>
                <Badge className="bg-[#f3f0ff] text-[#5b3cc4]">Available: Starter tier+</Badge>
              </CardContent></Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Pricing */}
        <div className="mt-12 text-center">
          <h2 className="font-heading text-xl font-bold mb-2">Widget Access by Plan</h2>
          <p className="text-sm text-muted-foreground mb-6">More features unlock at higher tiers</p>
          <div className="overflow-x-auto">
            <table className="w-full max-w-3xl mx-auto text-sm border rounded-xl overflow-hidden">
              <thead><tr className="bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold">Feature</th>
                <th className="px-3 py-3 text-center font-semibold">Free<br/><span className="text-muted-foreground font-normal text-xs"> Free</span></th>
                <th className="px-3 py-3 text-center font-semibold bg-[#f3f0ff]/50">Basic<br/><span className="text-[#5b3cc4] font-normal text-xs"> 47</span></th>
                <th className="px-3 py-3 text-center font-semibold bg-[#f3f0ff]/50">Starter<br/><span className="text-[#5b3cc4] font-normal text-xs"> 97</span></th>
                <th className="px-3 py-3 text-center font-semibold bg-[#f3f0ff]/50">Standard<br/><span className="text-[#5b3cc4] font-normal text-xs"> 197</span></th>
                <th className="px-3 py-3 text-center font-semibold bg-[#f3f0ff]/50">Pro<br/><span className="text-[#5b3cc4] font-normal text-xs"> 397</span></th>
              </tr></thead>
              <tbody>
                {[
                  { feature: 'Max Entrants', free: '8', basic: '8', starter: '25', standard: '99', pro: '400' },
                  { feature: 'Pool Standings', free: true, basic: true, starter: true, standard: true, pro: true },
                  { feature: 'Live Fixtures', free: false, basic: true, starter: true, standard: true, pro: true },
                  { feature: 'League Table', free: false, basic: true, starter: true, standard: true, pro: true },
                  { feature: 'Team Profiles', free: false, basic: true, starter: true, standard: true, pro: true },
                  { feature: 'Season Schedule', free: false, basic: false, starter: true, standard: true, pro: true },
                  { feature: 'Head-to-Head', free: false, basic: false, starter: true, standard: true, pro: true },
                  { feature: 'CSV Export', free: false, basic: false, starter: false, standard: true, pro: true },
                  { feature: 'Priority Support', free: false, basic: false, starter: false, standard: false, pro: true },
                ].map((row, i) => (
                  <tr key={row.feature} className="border-t">
                    <td className="px-4 py-2.5 text-sm font-medium">{row.feature}</td>
                    {['free','basic','starter','standard','pro'].map(plan => (
                      <td key={plan} className={`px-3 py-2.5 text-center ${plan !== 'free' ? 'bg-[#f3f0ff]/20' : ''}`}>
                        {typeof row[plan] === 'boolean' ? (
                          row[plan] ? <Check className="h-4 w-4 text-[#5b3cc4] mx-auto" /> : <span className="text-gray-300">—</span>
                        ) : (
                          <span className="text-sm font-semibold">{row[plan]}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 max-w-2xl mx-auto">
          <Card className="bg-[#f3f0ff]/30 border-[#5b3cc4]/20">
            <CardContent className="pt-4">
              <h3 className="font-semibold text-sm mb-1">Setup Note</h3>
              <p className="text-xs text-muted-foreground">To enable live widgets, whitelist your domain (poolsfc.co) in the <a href="https://dashboard.api-football.com/profile?access" target="_blank" rel="noreferrer" className="text-[#5b3cc4] underline">API-Football dashboard</a>. One-time setup. Previews above show exactly what widgets display with real-time data.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
